/***
 * Created on 2024/4/16
 * @author: Adam Cheng <adamcheng@qnap.com>
 ***/

import React from 'react';
import ModalDialog from "../../../../../common/modal_dialog/modalDialog";
import {useTranslation} from "react-i18next";
import device_phone_svg from "../../../../../../static/img/utils/ic--device--phone.svg";
import {formatDate, formatTime} from "../../../../../../service/datetime";
import {getLocalUser} from "../../../../../../service/user";
import {QNAP_AUTH_APP_METHOD_MAP} from "./qnap_auth_panel_row";
import {useMutation} from "@tanstack/react-query";
import {apiDelete, apiPost} from "../../../../../../service/api";

function DeviceInfoRow({device}) {
    const { t } = useTranslation();

    return (
        <div className="flex-table-row">
            <div className="flex-table__cell flex-table-row__cell w-35" data-label="Device Name">
                <div className="comp-group-info">
                    <div className="group-info--img round-full">
                        <img src={device_phone_svg} alt=""/>
                    </div>
                    <div className="group-info--detail">
                        <div className="group-info--detail__main-text">{device?.device?.model_name}</div>
                    </div>
                </div>
            </div>
            <div className="flex-table__cell flex-table-row__cell w-32" data-label="Modal/OS Version">
                <div className="cell-text mb-4">{device?.device?.model_name}</div>
                <div className="cell-text">{device?.device?.device_os}</div>
            </div>
            <div className="flex-table__cell flex-table-row__cell w-33" data-label="Date Added">
                <span className="cell-text">{formatDate(t, device?.added_on, true)} {formatTime(device?.added_on)}</span>
            </div>
        </div>
    );
}

function ManageSetupDialog({method, dispatch, state, ...props}) {
    const { t } = useTranslation();
    const currentUser = getLocalUser();
    const [action, setAction] = React.useState('reuse');
    const authMethod = method || state.method;

    const disableMutate = useMutation({
        mutationFn: async (data) => await apiPost('/ajax/qnap_auth/method', data),
        onSuccess: async (data) => {
            dispatch({type: 'toggleQnapAuthSetupSuccess', value: true, method: QNAP_AUTH_APP_METHOD_MAP[authMethod]});
        }
    });

    const resetQnapAuthMutate = useMutation({
        mutationFn: async (data) => await apiDelete('/ajax/qnap_auth/method', data),
        onSuccess: async (data) => {
            dispatch({
                type: "toggleShowInstallApp",
                value: true,
                openAfterInstallApp: "toggleShowQrCodeScan",
                method: QNAP_AUTH_APP_METHOD_MAP[authMethod],
                setupAction: action
            });
        }
    })

    const onApply = async () => {
        if (action === 'reuse') {
            await disableMutate.mutateAsync({
                method: QNAP_AUTH_APP_METHOD_MAP[authMethod] || authMethod,
                is_enabled: true
            });
        }
        else {
            // clear current qnap_auth data and rebind.
            await resetQnapAuthMutate.mutateAsync({});
        }
    }

    return (
        <ModalDialog {...props} title={t('@Set up through QNAP Authenticator@')} submitText={t('@Apply@')} submitAction={onApply}>
            <div className="dialog-content dialog-content--two-step">
                <p className="!mb-24">
                    {t('@Enabling {{authentication_method}} method will affect the existing pairings on your mobile devices. Would you like to enable this method alongside the existing ones on these paired devices?@', {authentication_method: authMethod})}
                </p>
                <div className="comp-radio">
                    <label className="q-radio" htmlFor="2fa-methods">
                        <input type="radio" name="2fa-methods" id="2fa-methods"
                               value={action} checked={action==='reuse'}
                               onChange={() => setAction(prev=> prev === 'reuse' ? 'reset' : 'reuse')}/>
                        {t('@Yes, I want to use these device for {{authentication_method}}@', {authentication_method: authMethod})}
                    </label>
                </div>
                <div className="two-step-methods--paired-devices-list pl-32 mt-16 mb-24">
                    <div className="comp-flex-table">
                        <div className="flex-table-wrapper">
                            <div className="flex-table-header">
                                <div
                                    className="flex-table__cell flex-table-header__cell w-35">{t('@Device Name@')}</div>
                                <div
                                    className="flex-table__cell flex-table-header__cell w-32">{t('@Modal/OS Version@')}</div>
                                <div className="flex-table__cell flex-table-header__cell w-33">{t('@Date Added@')}</div>
                            </div>
                            <div className="flex-table-body">
                                {
                                    (Object.values(currentUser?.secure?.qnap_auth?.devices ?? {})).map((device) => {
                                        return (<DeviceInfoRow device={device} key={device.id} />);
                                    })
                                }
                            </div>
                        </div>
                    </div>
                </div>
                <div className="comp-radio">
                    <label className="q-radio" htmlFor="2fa-methods-reset">
                        <input type="radio" name="2fa-methods" id="2fa-methods-reset"
                               value={action.value} checked={action.checked}
                               onChange={() => setAction(prev=> prev === 'reset' ? 'reuse' : 'reset')}/>
                        {t('@No, I want to configure a new device.@')}
                    </label>
                </div>
                <p className="text-remind pl-32 mt-10">
                    {t('@The existing paired devices will be deleted immediately. This action is irreversible.@')}
                </p>
            </div>
        </ModalDialog>
    );
}

export default ManageSetupDialog;